import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { StatusChip, SubmitButton } from '../common';
import { useOptionalChatPanel } from '../../contexts/ChatPanelContext';
import {
  cancelPendingAction,
  confirmPendingAction,
  forgetOpenPending,
  listOpenPendingIds,
} from '../../services/chatService';
import { markConfirmation, mergeOutcomeLine, outcomeLine } from '../../utils/chatStreamReducer';
import { parseServerTime } from '../agent/workspace/time';
import { fill, useChatCardCopy, useChatCardTokens } from './chatCards';

// The state a card shows, from the outcome line, the card's own decision, the open list and
// the clock, in that order. Each maps to a chip role and a sentence in the copy.
const STATE_ROLE = {
  working: 'info',
  done: 'success',
  failed: 'error',
  cancelled: 'default',
  expired: 'default',
  closed: 'default',
};

const OUTCOME_STATE = {
  executed: 'done',
  failed: 'failed',
  expired: 'expired',
  cancelled: 'cancelled',
  confirmed: 'working',
};

/** "14:05" for 845 seconds; "1:02:05" past an hour. */
export const formatCountdown = (seconds) => {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
};

/**
 * A confirm card under a chat answer that asks the user to OK an action (publish, send, spend):
 * the property it is for, the summary, a countdown to its expiry, Confirm and Cancel.
 *
 * `action` is the answer's { type: 'request_confirmation', pending_id, action, summary,
 * expires_at, property_label }; `outcome` the conversation's outcome line for it ({ status,
 * text }), which the backend adds after a confirm; `confirmation` what this card recorded on
 * its message (a cancel adds no line). Confirm calls POST /api/actions/pending/{id}/confirm and
 * puts the outcome line it answers with right under the card; the rest of the chat stays as it
 * is. A run that fails is read back from the chat's history. The buttons stay disabled once a
 * decision is made or the card expires.
 */
const ConfirmActionCard = ({ action, outcome = null, confirmation = null }) => {
  const copy = useChatCardCopy()?.confirm;
  const tokens = useChatCardTokens();
  const chat = useOptionalChatPanel();
  const pendingId = action?.pending_id;
  const expiresAt = useMemo(() => parseServerTime(action?.expires_at), [action?.expires_at]);
  const [now, setNow] = useState(() => Date.now());
  // Whether the confirmation is still open on the server: null until known.
  const [stillOpen, setStillOpen] = useState(null);

  const local = confirmation?.status || null;
  const busy = local === 'confirming' || local === 'cancelling';
  const expired = Boolean(expiresAt) && now >= expiresAt.getTime();

  let state = 'open';
  if (outcome?.status) state = OUTCOME_STATE[outcome.status] || 'closed';
  else if (busy) state = 'busy';
  else if (local && local in STATE_ROLE) state = local;
  else if (expired) state = 'expired';
  else if (stillOpen === false) state = 'closed';

  // Tick while the card can still be confirmed.
  useEffect(() => {
    if (state !== 'open' || !expiresAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [state, expiresAt]);

  // A card loaded from history may have been cancelled already (a cancel adds no line).
  const checkOpen = state === 'open' && stillOpen === null;
  useEffect(() => {
    if (!checkOpen || !pendingId) return undefined;
    let active = true;
    listOpenPendingIds()
      .then((ids) => {
        if (active) setStillOpen(ids.has(pendingId));
      })
      .catch(() => {}); // unknown: leave the card open, the server checks again on Confirm
    return () => {
      active = false;
    };
  }, [checkOpen, pendingId]);

  const mark = (patch) => chat?.updateConversation((conversation) => markConfirmation(conversation, pendingId, patch));

  /** Put `line`, this card's outcome line, right under the card, recording `patch` on it. */
  const showLine = (line, patch) => chat?.updateConversation((conversation) => mergeOutcomeLine(
    markConfirmation(conversation, pendingId, patch),
    pendingId,
    line,
  ));

  /** The outcome line KeyTa wrote into the chat, or null. Never throws. */
  const findLine = async () => {
    try {
      const line = await chat?.findOutcomeLine?.(pendingId);
      return line || null;
    } catch (error) {
      console.error('Chat: reading the outcome of a confirmation failed', error);
      return null;
    }
  };

  const handleConfirm = async () => {
    mark({ status: 'confirming', error: null });
    let result;
    try {
      result = await confirmPendingAction(pendingId);
    } catch (error) {
      console.error('Chat: confirming an action failed', error);
      forgetOpenPending();
      // A run that failed is reported by the backend as an outcome line.
      const line = await findLine();
      if (line) {
        showLine(line, { status: null, error: null });
        return;
      }
      // services/api.js's ApiError keeps the HTTP status as statusCode.
      const status = Number(error?.statusCode ?? error?.status);
      if (status >= 400 && status < 500 && status !== 401 && status !== 403) {
        mark({ status: 'closed' });
      } else {
        mark({ status: null, error: 'unreachable' });
      }
      return;
    }
    forgetOpenPending();
    // The line the backend added, as it answered; else the one in the chat's history, else ours.
    const text = typeof result?.outcome === 'string' ? result.outcome.trim() : '';
    const line = text
      ? outcomeLine(pendingId, {
        text,
        status: 'executed',
        action: action?.action || '',
        threadId: result?.thread_id || null,
      })
      : (await findLine()) || outcomeLine(pendingId, {
        text: copy?.doneLine || '',
        status: 'executed',
        action: action?.action || '',
      });
    showLine(line, { status: 'done' });
  };

  const handleCancel = async () => {
    mark({ status: 'cancelling', error: null });
    try {
      await cancelPendingAction(pendingId);
      forgetOpenPending();
      mark({ status: 'cancelled' });
    } catch (error) {
      console.error('Chat: cancelling an action failed', error);
      forgetOpenPending();
      const status = Number(error?.statusCode ?? error?.status);
      mark(status >= 400 && status < 500 && status !== 401 && status !== 403
        ? { status: 'closed' }
        : { status: null, error: 'unreachable' });
    }
  };

  if (!copy || !pendingId) return null;

  const open = state === 'open';
  const decided = !open && state !== 'busy';
  const remaining = expiresAt ? (expiresAt.getTime() - now) / 1000 : null;
  const sentence = {
    working: copy.working,
    done: copy.done,
    failed: copy.failed,
    cancelled: copy.cancelled,
    expired: copy.expired,
    closed: copy.closed,
    busy: copy.working,
  }[state];
  const chipLabel = {
    working: copy.working,
    done: copy.done,
    failed: copy.failed,
  }[state];
  const titleId = `confirm-card-${pendingId}`;

  return (
    <Paper
      elevation={0}
      role="group"
      aria-labelledby={titleId}
      data-testid="confirm-action-card"
      sx={{
        mt: 1.5,
        p: 1.75,
        borderRadius: 2.5,
        border: `1px solid ${open ? tokens.lineHover : tokens.line}`,
        backgroundColor: tokens.surface,
        ...tokens.enter,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          aria-hidden="true"
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: tokens.primary,
            backgroundColor: tokens.soft,
          }}
        >
          <HourglassTopRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
            <Typography id={titleId} variant="subtitle2" component="p" sx={{ color: tokens.slate[900] }}>
              {copy.title}
            </Typography>
            {chipLabel && <StatusChip value={state} role={STATE_ROLE[state]} label={chipLabel} />}
          </Stack>
          {action.property_label && (
            <Typography variant="body2" sx={{ color: tokens.slate[600], mt: 0.25, overflowWrap: 'anywhere' }}>
              {fill(copy.forProperty, { address: action.property_label })}
            </Typography>
          )}
          {action.summary && (
            <Typography variant="body2" sx={{ color: tokens.slate[800], mt: 0.5, overflowWrap: 'anywhere' }}>
              {action.summary}
            </Typography>
          )}
        </Box>
      </Stack>

      <Box role="status" aria-live="polite" sx={{ mt: decided || confirmation?.error ? 1 : 0 }}>
        {decided && sentence && (
          <Typography variant="body2" sx={{ color: tokens.slate[600] }}>{sentence}</Typography>
        )}
        {open && confirmation?.error && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>{copy.error}</Typography>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 1.25 }}
      >
        {open && remaining !== null && (
          <Typography
            variant="caption"
            sx={{ color: tokens.slate[600], mr: 'auto', fontVariantNumeric: 'tabular-nums' }}
          >
            {fill(copy.expiresIn, { time: formatCountdown(remaining) })}
          </Typography>
        )}
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={handleCancel}
          disabled={!open}
        >
          {copy.cancel}
        </Button>
        <SubmitButton
          type="button"
          variant="contained"
          size="small"
          onClick={handleConfirm}
          disabled={!open}
          pending={local === 'confirming'}
        >
          {copy.confirm}
        </SubmitButton>
      </Stack>
    </Paper>
  );
};

export default ConfirmActionCard;
